import React from 'react';

import { sig } from '@stratumn/js-crypto';
import compose from 'lodash.flowright';
import { KeyRound, LockOpen } from 'lucide-react';
import PropTypes from 'prop-types';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';
import { Input } from '@/shadcn/input';
import { Label } from '@/shadcn/label';
import { Tooltip, TooltipContent, TooltipProvider } from '@/shadcn/tooltip';

import { notify } from '@/components/toast';
import { Prose } from '@/components/ui/prose/prose';
import { withUser } from '@/contexts';
import { SpanType, withSpan } from '@/tracing';
import { TestId } from '@/types/testId';
import { deepGet } from '@/utils';

export class SignLinkDialog extends React.PureComponent {
  static propTypes = {
    user: PropTypes.object.isRequired,
    onSendLink: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    children: PropTypes.node,
    disabled: PropTypes.bool,
    focusElement: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
      PropTypes.func
    ])
  };

  static defaultProps = {
    description: null,
    children: null,
    disabled: false,
    focusElement: null
  };

  state = {
    password: undefined,
    wrongPassword: false
  };

  handlePasswordChange = e => {
    this.setState({ password: e.target.value, wrongPassword: false });
  };

  // ==========================================================================
  // Get the user password if the key is protected
  // ==========================================================================

  decryptWallet = () => {
    const {
      user: {
        setPassword,
        me: {
          account: { signingKey }
        }
      }
    } = this.props;
    const { password } = this.state;

    try {
      if (deepGet(signingKey, 'privateKey.passwordProtected', false)) {
        new sig.SigningPrivateKey({
          pemPrivateKey: signingKey.privateKey.decrypted,
          password
        }).export();
      }
    } catch {
      this.setState({ wrongPassword: true });
      return;
    }

    setPassword(password);
  };

  handleKeyPress = e => {
    if (e.key === 'Enter') {
      this.decryptWallet();
    }
  };

  renderPasswordEntry = () => {
    const { wrongPassword, password } = this.state;

    return (
      <div className="flex flex-col items-center">
        <div className="my-6 flex w-full flex-col items-center gap-6 text-muted-foreground text-sm">
          <KeyRound size={32} />
          <p>Your key is password protected.</p>
        </div>
        <div className="flex w-2/3 flex-col items-start gap-2">
          <Label>Enter your password to decrypt your key</Label>
          <div className="flex w-full items-center gap-1">
            <Input
              className="w-full"
              invalid={wrongPassword}
              onChange={this.handlePasswordChange}
              onKeyPress={this.handleKeyPress}
              placeholder="Password"
              type="password"
              value={password}
            />
            <TooltipProvider>
              <Tooltip>
                <Button
                  aria-label="Submit password"
                  data-cy="decrypt-key-button"
                  data-testid={TestId.SignLinkDialog.DecryptButton}
                  onClick={this.decryptWallet}
                  size="icon"
                  variant="default"
                >
                  <LockOpen />
                </Button>
                <TooltipContent>Click to submit password</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {wrongPassword && (
            <p
              className="text-destructive text-xs"
              data-cy="wrong-password-message"
            >
              The password you entered is incorrect.
            </p>
          )}
        </div>
      </div>
    );
  };

  // ==========================================================================
  // Use the key to sign the link
  // ==========================================================================

  getSigningPrivateKey = () => {
    const {
      user: {
        me: {
          account: { signingKey }
        },
        password
      }
    } = this.props;

    const { passwordProtected, decrypted } = signingKey.privateKey;

    if (passwordProtected)
      return new sig.SigningPrivateKey({
        pemPrivateKey: decrypted,
        password
      }).export();

    return new sig.SigningPrivateKey({
      pemPrivateKey: decrypted
    }).export();
  };

  createLink = () =>
    withSpan('signLink', SpanType.crypto, () => {
      const { onSendLink } = this.props;

      let signingKey;
      try {
        signingKey = this.getSigningPrivateKey();
      } catch (err) {
        notify.error(err);
        return;
      }

      onSendLink(signingKey).catch(err => {
        console.error(err);
        notify.error('Something went wrong while creating a link.');
      });
    });

  renderNoKey = () => {
    const { title, onClose, open } = this.props;

    return (
      <Dialog onOpenChange={onClose} open={open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Your private signing key is not available through your Stratumn
            Account.
          </p>
          <p className="text-sm">
            To perform actions on Trace, please update your account.
          </p>
        </DialogContent>
      </Dialog>
    );
  };

  renderSignLink = () => {
    const { onClose, disabled, open, children, user } = this.props;

    if (!open) return null;
    const {
      me: {
        account: { signingKey }
      }
    } = user;

    if (!signingKey?.privateKey) return this.renderNoKey();

    return (
      <>
        {children && <Prose text={children} />}
        <DialogFooter className="pt-2">
          <DialogClose asChild>
            <Button
              data-cy="cancel-sign-button"
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            data-cy="sign-link-button"
            data-testid={TestId.SignLinkDialog.SignButton}
            disabled={disabled}
            onClick={this.createLink}
          >
            Sign action
          </Button>
        </DialogFooter>
      </>
    );
  };

  render() {
    const { title, onClose, open, user, description } = this.props;

    if (!open) return null;
    const {
      me: {
        account: { signingKey }
      },
      password
    } = user;

    if (!signingKey?.privateKey) return this.renderNoKey();

    const passwordPrompt = signingKey.privateKey.passwordProtected && !password;

    return (
      <Dialog onOpenChange={onClose} open={open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {description && <Prose text={description} />}
            </DialogDescription>
          </DialogHeader>
          {passwordPrompt ? this.renderPasswordEntry() : this.renderSignLink()}
        </DialogContent>
      </Dialog>
    );
  }
}

export default compose(withUser)(SignLinkDialog);
